'use client';

//
// Third Party
import * as React from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Dialog, DialogBackdrop } from '@headlessui/react';
import { useMounted } from '@hooks';
import { Typography } from '@components/typography';
import { Button } from '@components/button';

type Props = {
  isOpen: boolean;
  closeModal: VoidFunction;
  phoneNumber: string;
};

export const PhoneNumberModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  phoneNumber,
}) => {
  const mounted = useMounted();
  const focusDiv = React.useRef(null);

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <Dialog
      initialFocus={focusDiv}
      as="div"
      className="fixed inset-0 overflow-y-auto z-50 font-sans !font-[Roobert]"
      onClose={closeModal}
      open={isOpen}
    >
      <div ref={focusDiv} className="min-h-screen px-4 text-center">
        <DialogBackdrop className="fixed inset-0 bg-black opacity-60" />
        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-md px-6 py-12 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="mt-2 flex flex-row justify-center">
            <Typography
              component="a"
              className="text-center text-primary-main font-semibold text-lg"
              href={parsePhoneNumberFromString(phoneNumber)!.getURI()}
            >
              Would you like to call{' '}
              {parsePhoneNumberFromString(phoneNumber)!.formatNational()}?
            </Typography>
          </div>

          <div className="mt-8 flex flex-row justify-center">
            <Button variant="outlined" onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
